import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * ADF Template from database
 */
export interface AdfTemplate {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  element_slugs: string[];
  enable_trade_in: boolean;
  enable_co_buyer: boolean;
  display_order: number | null;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
}

/**
 * Hook to fetch active ADF templates from the database
 */
export function useAdfTemplates() {
  return useQuery({
    queryKey: ['adf-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('adf_templates')
        .select('id, slug, name, description, element_slugs, enable_trade_in, enable_co_buyer, display_order, is_active, created_at, updated_at')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;

      // Cast element_slugs from Json to string[] and ensure boolean defaults
      return (data || []).map(template => ({
        ...template,
        element_slugs: (template.element_slugs as unknown) as string[],
        enable_trade_in: template.enable_trade_in ?? false,
        enable_co_buyer: template.enable_co_buyer ?? false,
      })) as AdfTemplate[];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - static reference data
  });
}

/**
 * Get a template by slug from the templates array
 */
export function getTemplateBySlug(
  templates: AdfTemplate[] | undefined,
  slug: string
): AdfTemplate | undefined {
  return templates?.find(t => t.slug === slug);
}

/**
 * Convert templates array to a slug-indexed lookup object
 */
export function templatesToLookup(
  templates: AdfTemplate[] | undefined
): Record<string, AdfTemplate> {
  if (!templates) return {};
  return templates.reduce((acc, template) => {
    acc[template.slug] = template;
    return acc;
  }, {} as Record<string, AdfTemplate>);
}
